import React, { useCallback, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

export type SourceField = 'aiCharacterRole' | 'aiCharacterName';

export type UseAutoReplaceFieldsReturn = {
  /** Ref to track previous value of aiCharacterRole */
  prevAiCharacterRoleRef: React.MutableRefObject<string>;
  /** Ref to track previous value of aiCharacterName */
  prevAiCharacterNameRef: React.MutableRefObject<string>;
  /** Handler for aiCharacterRole onBlur event */
  handleAiCharacterRoleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Handler for aiCharacterName onBlur event */
  handleAiCharacterNameBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** State indicating which fields were updated */
  updatedFields: { aiCharacterRole: boolean; aiCharacterName: boolean };
  /** Function to reset the updated fields status */
  resetUpdatedFields: () => void;
};

/**
 * Hook to handle auto-replacement of values across AI Role Play form fields.
 * When aiCharacterRole or aiCharacterName changes (on blur), this hook automatically
 * replaces all occurrences of the old value with the new value in related fields
 * (context, description, aiCharacterObjections, aiLearnerGoals, requiredAiLearnerGoal).
 */
const useAutoReplaceFields = (): UseAutoReplaceFieldsReturn => {
  const {
    setValue,
    getValues,
    reset,
  } = useFormContext();

  // State to track which fields were updated (for showing status message)
  const [updatedFields, setUpdatedFields] = React.useState<{
    aiCharacterRole: boolean;
    aiCharacterName: boolean;
  }>({ aiCharacterRole: false, aiCharacterName: false });

  // Refs to track previous values for aiCharacterRole and aiCharacterName
  const prevAiCharacterRoleRef = useRef<string>(getValues().aiCharacterRole || '');
  const prevAiCharacterNameRef = useRef<string>(getValues().aiCharacterName || '');

  /**
   * Replaces all occurrences of oldValue with newValue (case-insensitive).
   * Uses indexOf with lowercase comparison for simple and efficient matching.
   */
  const replaceAllOccurrences = useCallback((text: string, oldValue: string, newValue: string): string => {
    if (!text) return text;

    const lowerText = text.toLowerCase();
    const lowerOldValue = oldValue.toLowerCase();
    let result = '';
    let lastIndex = 0;
    let index = lowerText.indexOf(lowerOldValue);

    while (index !== -1) {
      result += text.slice(lastIndex, index) + newValue;
      lastIndex = index + oldValue.length;
      index = lowerText.indexOf(lowerOldValue, lastIndex);
    }

    return result + text.slice(lastIndex);
  }, []);

  /**
   * Replaces all occurrences of oldValue with newValue in the given text fields.
   * This function updates context, description, aiCharacterObjections, aiLearnerGoals,
   * requiredAiLearnerGoal, and optionally aiCharacterRole/aiCharacterName fields.
   * @param oldValue - The value to search for
   * @param newValue - The value to replace with
   * @param sourceField - The field that triggered the change ('aiCharacterRole' or 'aiCharacterName')
   */
  const replaceValueInFields = useCallback((
    oldValue: string,
    newValue: string,
    sourceField: SourceField,
  ) => {
    if (!oldValue || oldValue === newValue) return;

    const currentValues = getValues();

    // Update aiCharacterRole if source is aiCharacterName (name can be used in role)
    if (sourceField === 'aiCharacterName' && currentValues.aiCharacterRole) {
      const updatedRole = replaceAllOccurrences(currentValues.aiCharacterRole, oldValue, newValue);
      if (updatedRole !== currentValues.aiCharacterRole) {
        setValue('aiCharacterRole', updatedRole, { shouldDirty: true });
        // Also update the ref since we're changing the role value
        prevAiCharacterRoleRef.current = updatedRole;
      }
    }

    // Update aiCharacterName if source is aiCharacterRole (role can be used in name)
    if (sourceField === 'aiCharacterRole' && currentValues.aiCharacterName) {
      const updatedName = replaceAllOccurrences(currentValues.aiCharacterName, oldValue, newValue);
      if (updatedName !== currentValues.aiCharacterName) {
        setValue('aiCharacterName', updatedName, { shouldDirty: true });
        // Also update the ref since we're changing the name value
        prevAiCharacterNameRef.current = updatedName;
      }
    }

    // Update context field
    if (currentValues.context) {
      const updatedContext = replaceAllOccurrences(currentValues.context, oldValue, newValue);
      if (updatedContext !== currentValues.context) {
        setValue('context', updatedContext, { shouldDirty: true });
      }
    }

    // Update description field
    if (currentValues.description) {
      const updatedDescription = replaceAllOccurrences(currentValues.description, oldValue, newValue);
      if (updatedDescription !== currentValues.description) {
        setValue('description', updatedDescription, { shouldDirty: true });
      }
    }

    // Update aiCharacterObjections (array of { id, text })
    if (currentValues.aiCharacterObjections?.length) {
      currentValues.aiCharacterObjections.forEach((objection, index) => {
        if (objection?.text) {
          const updatedText = replaceAllOccurrences(objection.text, oldValue, newValue);
          if (updatedText !== objection.text) {
            setValue(`aiCharacterObjections.${index}.text`, updatedText, { shouldDirty: true });
          }
        }
      });
    }

    // Update requiredAiLearnerGoal ({ id, text })
    if (currentValues.requiredAiLearnerGoal?.text) {
      const updatedText = replaceAllOccurrences(currentValues.requiredAiLearnerGoal.text, oldValue, newValue);
      if (updatedText !== currentValues.requiredAiLearnerGoal.text) {
        setValue('requiredAiLearnerGoal.text', updatedText, { shouldDirty: true });
      }
    }

    // Update aiLearnerGoals (array of { id, text })
    if (currentValues.aiLearnerGoals?.length) {
      currentValues.aiLearnerGoals.forEach((goal, index) => {
        if (goal?.text) {
          const updatedText = replaceAllOccurrences(goal.text, oldValue, newValue);
          if (updatedText !== goal.text) {
            // Update each field individually for field array
            setValue(`aiLearnerGoals.${index}.text`, updatedText, { shouldDirty: true });
          }
        }
      });
    }

    // Force re-render of all form fields by resetting with updated values
    // This is needed because setValue doesn't trigger re-renders for uncontrolled inputs
    // managed by useFieldArray in other components.
    // Note: This is called on blur, so cursor position/focus issues are not a concern.
    const updatedValues = getValues();
    reset(updatedValues, {
      keepDirty: true,
      keepDefaultValues: true,
      keepErrors: true,
      keepTouched: true,
      keepIsValid: true,
    });

    // Show updated status for fields that were changed
    if (sourceField === 'aiCharacterRole') {
      setUpdatedFields((prev) => ({ ...prev, aiCharacterRole: true }));
    }
    if (sourceField === 'aiCharacterName') {
      setUpdatedFields((prev) => ({ ...prev, aiCharacterName: true }));
    }
  }, [
    getValues,
    setValue,
    replaceAllOccurrences,
    reset,
  ]);

  const handleAiCharacterRoleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const oldValue = prevAiCharacterRoleRef.current;

    if (oldValue && newValue && oldValue !== newValue) {
      replaceValueInFields(oldValue, newValue, 'aiCharacterRole');
    }

    // Update the ref to the new value
    prevAiCharacterRoleRef.current = newValue;
  }, [replaceValueInFields]);

  const handleAiCharacterNameBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const oldValue = prevAiCharacterNameRef.current;

    if (oldValue && newValue && oldValue !== newValue) {
      replaceValueInFields(oldValue, newValue, 'aiCharacterName');
    }

    // Update the ref to the new value
    prevAiCharacterNameRef.current = newValue;
  }, [replaceValueInFields]);

  const resetUpdatedFields = useCallback(() => {
    setUpdatedFields({ aiCharacterRole: false, aiCharacterName: false });
  }, []);

  return {
    prevAiCharacterRoleRef,
    prevAiCharacterNameRef,
    handleAiCharacterRoleBlur,
    handleAiCharacterNameBlur,
    updatedFields,
    resetUpdatedFields,
  };
};

export default useAutoReplaceFields;
